// Argentine-specific formatting utilities

/**
 * Format number using Argentine locale (comma as decimal separator)
 */
export const formatNumber = (value: number): string => {
  return new Intl.NumberFormat('es-AR').format(value);
};

/**
 * Format currency using Argentine Peso
 */
export const formatCurrency = (value: number, currency: string = 'ARS'): string => {
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

/**
 * Format phone number for display (removes country code for local display)
 */
export const formatPhoneNumber = (phone: string): string => {
  if (!phone) return '';
  
  // Remove all non-digit characters
  const cleaned = phone.replace(/\D/g, '');
  
  // If it starts with 54 (Argentina country code), format as local
  if (cleaned.startsWith('54')) {
    const remaining = cleaned.slice(2);
    if (remaining.length === 10) {
      return `${remaining.slice(0, 2)} ${remaining.slice(2, 4)} ${remaining.slice(4, 8)}-${remaining.slice(8)}`;
    }
  }
  
  // If it's a local number (10 digits), format it
  if (cleaned.length === 10) {
    return `${cleaned.slice(0, 2)} ${cleaned.slice(2, 4)} ${cleaned.slice(4, 8)}-${cleaned.slice(8)}`;
  }
  
  // Return as is if it doesn't match expected formats
  return phone;
};

/**
 * Validate Argentine phone number format
 */
export const validateArgentinePhone = (phone: string): boolean => {
  const cleaned = phone.replace(/\D/g, '');
  
  // Argentine mobile numbers are typically 10 digits (with country code: 54 + 9 + area code + number)
  // or 11 digits (with country code: 54 + area code + number)
  return cleaned.length === 10 || cleaned.length === 11;
};

/**
 * Format date using Argentine locale
 */
export const formatDate = (date: Date): string => {
  return new Intl.DateTimeFormat('es-AR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(date);
};

/**
 * Format date in Argentine format (dd-MM-YYYY)
 */
export const formatDateArgentine = (date: Date | string): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  if (isNaN(dateObj.getTime())) {
    return '';
  }
  
  const day = dateObj.getDate().toString().padStart(2, '0');
  const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
  const year = dateObj.getFullYear();
  
  return `${day}-${month}-${year}`;
};

/**
 * Convert Argentine date format (dd-MM-YYYY) to ISO format for input fields
 */
export const convertArgentineToISO = (argentineDate: string): string => {
  if (!argentineDate || argentineDate.length !== 10) {
    return '';
  }
  
  const parts = argentineDate.split('-');
  if (parts.length !== 3) {
    return '';
  }
  
  const [day, month, year] = parts;
  return `${year}-${month}-${day}`;
};

/**
 * Convert ISO date format (YYYY-MM-DD) to Argentine format
 */
export const convertISOToArgentine = (isoDate: string): string => {
  if (!isoDate || isoDate.length !== 10) {
    return '';
  }
  
  const parts = isoDate.split('-');
  if (parts.length !== 3) {
    return '';
  }
  
  const [year, month, day] = parts;
  return `${day}-${month}-${year}`;
};

/**
 * Format time using Argentine locale (24-hour format)
 */
export const formatTime = (date: Date): string => {
  return new Intl.DateTimeFormat('es-AR', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
}; 